import { useEffect, useId, useRef, useState } from 'react'
import type { PointerEvent } from 'react'

interface CoverPositionEditorProps {
  imageUrl: string
  position: number
  language: string
  onSave: (position: number) => Promise<void>
  onCancel: () => void
}

const clamp = (value: number) => Math.max(0, Math.min(100, Math.round(value)))

export default function CoverPositionEditor({ imageUrl, position, language, onSave, onCancel }: CoverPositionEditorProps) {
  const italian = language === 'it'
  const [draft, setDraft] = useState(() => clamp(position))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ pointerId: number; startY: number; position: number; overflow: number } | null>(null)
  const rangeId = useId()
  const hintId = useId()

  useEffect(() => {
    setDraft(clamp(position))
    setError(null)
  }, [imageUrl, position])

  const startDrag = (event: PointerEvent<HTMLDivElement>) => {
    const image = imageRef.current
    const preview = previewRef.current
    if (saving || !image || !preview || !image.naturalWidth || event.button !== 0) return
    const { width, height } = preview.getBoundingClientRect()
    const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight)
    const overflow = image.naturalHeight * scale - height
    if (overflow <= 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = { pointerId: event.pointerId, startY: event.clientY, position: draft, overflow }
  }

  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId || saving) return
    setDraft(clamp(drag.position - (event.clientY - drag.startY) / drag.overflow * 100))
  }

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const save = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await onSave(draft)
    } catch {
      setError(italian ? 'Impossibile salvare la posizione. Riprova.' : 'Unable to save the position. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="absolute inset-0 z-30 rounded-xl" aria-busy={saving}>
      <div
        ref={previewRef}
        className="absolute inset-0 touch-none select-none overflow-hidden bg-neutral-900 cursor-grab active:cursor-grabbing"
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={() => { dragRef.current = null }}
      >
        <img ref={imageRef} src={imageUrl} alt={italian ? 'Anteprima della copertina' : 'Cover preview'} draggable={false} className="h-full w-full object-cover" style={{ objectPosition: `50% ${draft}%` }} />
      </div>
      <div className="absolute inset-x-2 bottom-2 rounded-2xl border border-white/40 bg-white/90 p-3 text-foreground shadow-lg backdrop-blur sm:inset-x-3 sm:bottom-3 sm:p-4">
        <div className="flex items-center justify-between gap-3">
          <p id={hintId} className="min-w-0 text-xs text-muted-foreground sm:text-sm">
            {italian ? 'Trascina la copertina in verticale o usa il cursore per riposizionarla.' : 'Drag the cover vertically or use the slider to reposition it.'}
          </p>
          <output htmlFor={rangeId} className="shrink-0 text-sm">{draft}%</output>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <input id={rangeId} type="range" min={0} max={100} step={1} value={draft} disabled={saving} aria-describedby={hintId} onChange={(event) => setDraft(Number(event.target.value))} className="min-w-0 flex-1 accent-primary rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50" />
          <button type="button" disabled={saving} onClick={onCancel} className="shrink-0 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50">{italian ? 'Annulla' : 'Cancel'}</button>
          <button type="button" disabled={saving} onClick={save} className="shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50">{saving ? (italian ? 'Salvataggio…' : 'Saving…') : (italian ? 'Salva' : 'Save')}</button>
        </div>
        {error && <p role="alert" className="mt-3 text-sm text-foreground">{error}</p>}
      </div>
    </div>
  )
}
